import React, { useEffect } from 'react';
import { Modal, Form, Row, Col, Alert } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { useToast } from 'providers/ToastProvider';
import Button from 'components/base/Button';
import { useAtualizarTributacaoItem } from '../../../../hooks/useNFe';
import { ItemNFe, AtualizarTributacaoItemRequest } from '../../../../types/nfe';
import SeletorTributacaoICMS, {
  isCSTComST,
  isCSTRetidoAntes,
  isCSTSemICMS,
  isCSTComReducaoBC,
  isCSTComCreditoSN,
} from '../../components/SeletorTributacaoICMS';
import SelectCSTIPI from '../../components/SelectCSTIPI';
import SelectCSTContribuicao from '../../components/SelectCSTContribuicao';

interface ModalTributacaoItemNFeProps {
  show: boolean;
  onHide: () => void;
  nfeId: string;
  item: ItemNFe;
}

type FormValues = AtualizarTributacaoItemRequest;

const buildDefaultValues = (item: ItemNFe): FormValues => {
  const t = item.tributacao;
  return {
    origem: t.origem ?? '0',
    cst: t.cst?.trim(),
    csosn: t.csosn?.trim(),
    reducaoBC: t.reducaoBcIcms,
    baseCalculoICMS: t.baseCalculoIcms,
    aliquotaICMS: t.aliquotaIcms,
    valorICMS: t.valorIcms,
    aliquotaCreditoSN: t.aliquotaCreditoSN,
    aliquotaFCP: t.aliquotaFcp,
    aliquotaFCPST: t.aliquotaFcpSt,
    mva: t.mva,
    reducaoBCST: t.reducaoBcSt,
    baseCalculoST: t.baseCalculoSt,
    aliquotaST: t.aliquotaSt,
    valorST: t.valorSt,
    baseSTRetido: t.baseStRetido,
    valorSTRetido: t.valorStRetido,
    cstipi: t.cstIpi,
    baseCalculoIPI: t.baseCalculoIpi,
    aliquotaIPI: t.aliquotaIpi,
    valorIPI: t.valorIpi,
    cstpis: t.cstPis,
    baseCalculoPIS: t.baseCalculoPis,
    aliquotaPIS: t.aliquotaPis,
    valorPIS: t.valorPis,
    cstcofins: t.cstCofins,
    baseCalculoCOFINS: t.baseCalculoCofins,
    aliquotaCOFINS: t.aliquotaCofins,
    valorCOFINS: t.valorCofins,
  };
};

const numField = { setValueAs: (v: string) => (v === '' ? undefined : Number(v)) };
const round2 = (v: number) => Math.round(v * 100) / 100;

const ModalTributacaoItemNFe: React.FC<ModalTributacaoItemNFeProps> = ({ show, onHide, nfeId, item }) => {
  const { showToast } = useToast();

  const mutation = useAtualizarTributacaoItem(nfeId, item.id);

  const { register, handleSubmit, formState: { errors }, reset, watch, control, setValue } =
    useForm<FormValues>({ defaultValues: buildDefaultValues(item) });

  const calculosAtivos = React.useRef(false);

  useEffect(() => {
    if (show) {
      calculosAtivos.current = false;
      const v = buildDefaultValues(item);
      reset(v);
      if (v.cst) setValue('cst', v.cst);
      if (v.csosn) setValue('csosn', v.csosn);
      if (v.cstpis) setValue('cstpis', v.cstpis);
      if (v.cstcofins) setValue('cstcofins', v.cstcofins);
      if (v.cstipi) setValue('cstipi', v.cstipi);
      const t = setTimeout(() => { calculosAtivos.current = true; }, 0);
      return () => clearTimeout(t);
    }
  }, [show, item, reset, setValue]);

  const [bcICMS, aliqICMS] = watch(['baseCalculoICMS', 'aliquotaICMS']);
  const [bcST, aliqST, vICMSAtual] = watch(['baseCalculoST', 'aliquotaST', 'valorICMS']);
  const [bcPIS, aliqPIS] = watch(['baseCalculoPIS', 'aliquotaPIS']);
  const [bcCOFINS, aliqCOFINS] = watch(['baseCalculoCOFINS', 'aliquotaCOFINS']);
  const [bcIPI, aliqIPI] = watch(['baseCalculoIPI', 'aliquotaIPI']);

  useEffect(() => {
    if (!calculosAtivos.current || bcICMS == null || aliqICMS == null) return;
    setValue('valorICMS', round2(bcICMS * aliqICMS / 100));
  }, [bcICMS, aliqICMS, setValue]);

  useEffect(() => {
    if (!calculosAtivos.current || bcST == null || aliqST == null) return;
    // vICMS-ST = (BC-ST × alíquota) − ICMS da operação própria (regra geral do ICMS-ST)
    setValue('valorST', Math.max(0, round2(bcST * aliqST / 100 - (vICMSAtual ?? 0))));
  }, [bcST, aliqST, vICMSAtual, setValue]);

  useEffect(() => {
    if (!calculosAtivos.current || bcPIS == null || aliqPIS == null) return;
    setValue('valorPIS', round2(bcPIS * aliqPIS / 100));
  }, [bcPIS, aliqPIS, setValue]);

  useEffect(() => {
    if (!calculosAtivos.current || bcCOFINS == null || aliqCOFINS == null) return;
    setValue('valorCOFINS', round2(bcCOFINS * aliqCOFINS / 100));
  }, [bcCOFINS, aliqCOFINS, setValue]);

  useEffect(() => {
    if (!calculosAtivos.current || bcIPI == null || aliqIPI == null) return;
    setValue('valorIPI', round2(bcIPI * aliqIPI / 100));
  }, [bcIPI, aliqIPI, setValue]);

  const cstOuCsosn = watch('csosn') || watch('cst');
  const comST = isCSTComST(cstOuCsosn);
  const retidoAntes = isCSTRetidoAntes(cstOuCsosn);
  const semICMS = isCSTSemICMS(cstOuCsosn);
  const comReducaoBC = isCSTComReducaoBC(cstOuCsosn);
  const comCreditoSN = isCSTComCreditoSN(cstOuCsosn);

  const onSubmit = async (data: FormValues) => {
    try {
      await mutation.mutateAsync(data);
      showToast('Tributação atualizada', 'success');
      onHide();
    } catch {
      // error shown via Alert
    }
  };

  return (
    <Modal show={show} onHide={() => !mutation.isPending && onHide()} size="lg" centered>
      <Modal.Header closeButton className="border-bottom border-opacity-25 pb-3">
        <div>
          <div className="nfe-dest-section-title mb-1">Tributação de Item</div>
          <div className="d-flex align-items-baseline gap-2 flex-wrap">
            <span className="nfe-mono fw-semibold text-primary" style={{ fontSize: '0.78rem' }}>
              {item.codigoProduto}
            </span>
            <span className="fw-semibold text-body-emphasis" style={{ fontSize: '0.88rem', lineHeight: 1.3 }}>
              {item.nomeProduto}
            </span>
          </div>
        </div>
      </Modal.Header>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body className="p-4">
          {mutation.isError && (
            <Alert variant="danger" className="border border-danger border-opacity-25 bg-danger-subtle py-2 mb-3">
              <small className="text-danger-emphasis">Erro ao salvar tributação. Tente novamente.</small>
            </Alert>
          )}

          {/* ── ICMS ─────────────────────────────────── */}
          <div className="nfe-dest-section">
            <div className="nfe-dest-section-header">
              <span className="nfe-dest-section-title">ICMS</span>
            </div>
            <Row className="g-3">
              <Col xs={12} md={5}>
                <Form.Group>
                  <label className="nfe-dest-label">Origem</label>
                  <Form.Select size="sm" {...register('origem')} disabled={mutation.isPending}>
                    <option value="0">0 — Nacional</option>
                    <option value="1">1 — Estrangeira (import. direta)</option>
                    <option value="2">2 — Estrangeira (merc. interno)</option>
                    <option value="3">3 — Nacional (&gt;40% importado)</option>
                    <option value="4">4 — Nacional (proc. produtivos)</option>
                    <option value="5">5 — Nacional (&lt;40% importado)</option>
                    <option value="6">6 — Estrangeira (sem similar)</option>
                    <option value="7">7 — Estrangeira (merc. int. sem similar)</option>
                    <option value="8">8 — Nacional (&gt;70% importado)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} md={7}>
                <Form.Group>
                  <label className="nfe-dest-label">CST/CSOSN</label>
                  <SeletorTributacaoICMS
                    size="sm"
                    value={cstOuCsosn ?? ''}
                    onChange={(value) => {
                      const isCsosn = value.length === 3;
                      setValue('cst', isCsosn ? undefined : value || undefined);
                      setValue('csosn', isCsosn ? value : undefined);
                    }}
                    disabled={mutation.isPending}
                  />
                </Form.Group>
              </Col>

              {!semICMS && !retidoAntes && (
                <>
                  {comReducaoBC && (
                    <Col xs={6} md={3}>
                      <Form.Group>
                        <label className="nfe-dest-label">Red. Base ICMS %</label>
                        <Form.Control size="sm" type="number" step="0.0001" {...register('reducaoBC', numField)} disabled={mutation.isPending} placeholder="0,0000" />
                      </Form.Group>
                    </Col>
                  )}
                  <Col xs={12} md={comReducaoBC ? 3 : 4}>
                    <Form.Group>
                      <label className="nfe-dest-label">Base Cálc. ICMS</label>
                      <Form.Control size="sm" type="number" step="0.01" {...register('baseCalculoICMS', numField)} disabled={mutation.isPending} placeholder="0,00" />
                    </Form.Group>
                  </Col>
                  <Col xs={6} md={comReducaoBC ? 3 : 4}>
                    <Form.Group>
                      <label className="nfe-dest-label">Alíquota %</label>
                      <Form.Control size="sm" type="number" step="0.01" {...register('aliquotaICMS', numField)} disabled={mutation.isPending} placeholder="0,00" />
                    </Form.Group>
                  </Col>
                  <Col xs={6} md={comReducaoBC ? 3 : 4}>
                    <Form.Group>
                      <label className="nfe-dest-label">Valor ICMS</label>
                      <Form.Control size="sm" type="number" step="0.01" {...register('valorICMS', numField)} disabled={mutation.isPending} placeholder="0,00" />
                    </Form.Group>
                  </Col>
                  {comCreditoSN && (
                    <Col xs={6} md={4}>
                      <Form.Group>
                        <label className="nfe-dest-label">Alíq. Cálc. Créd. %</label>
                        <Form.Control size="sm" type="number" step="0.01" {...register('aliquotaCreditoSN', numField)} disabled={mutation.isPending} placeholder="0,00" />
                      </Form.Group>
                    </Col>
                  )}
                  <Col xs={6} md={4}>
                    <Form.Group>
                      <label className="nfe-dest-label">Alíq. FCP %</label>
                      <Form.Control size="sm" type="number" step="0.01" {...register('aliquotaFCP', numField)} disabled={mutation.isPending} placeholder="0,00" />
                    </Form.Group>
                  </Col>
                </>
              )}

              {retidoAntes && (
                <>
                  <Col xs={12}>
                    <span className="nfe-dest-section-title" style={{ fontSize: '0.62rem' }}>ST COBRADO ANTERIORMENTE</span>
                  </Col>
                  <Col xs={6}>
                    <Form.Group>
                      <label className="nfe-dest-label">Base ST Retido</label>
                      <Form.Control size="sm" type="number" step="0.01" {...register('baseSTRetido', numField)} disabled={mutation.isPending} placeholder="0,00" />
                    </Form.Group>
                  </Col>
                  <Col xs={6}>
                    <Form.Group>
                      <label className="nfe-dest-label">Valor ST Retido</label>
                      <Form.Control size="sm" type="number" step="0.01" {...register('valorSTRetido', numField)} disabled={mutation.isPending} placeholder="0,00" />
                    </Form.Group>
                  </Col>
                </>
              )}

              {comST && (
                <>
                  <Col xs={12}>
                    <span className="nfe-dest-section-title" style={{ fontSize: '0.62rem' }}>ICMS-ST</span>
                  </Col>
                  <Col xs={6} md={4}>
                    <Form.Group>
                      <label className="nfe-dest-label">Base ST</label>
                      <Form.Control size="sm" type="number" step="0.01" {...register('baseCalculoST', numField)} disabled={mutation.isPending} placeholder="0,00" />
                    </Form.Group>
                  </Col>
                  <Col xs={6} md={4}>
                    <Form.Group>
                      <label className="nfe-dest-label">Alíquota ST %</label>
                      <Form.Control size="sm" type="number" step="0.01" {...register('aliquotaST', numField)} disabled={mutation.isPending} placeholder="0,00" />
                    </Form.Group>
                  </Col>
                  <Col xs={6} md={4}>
                    <Form.Group>
                      <label className="nfe-dest-label">Valor ST</label>
                      <Form.Control size="sm" type="number" step="0.01" {...register('valorST', numField)} disabled={mutation.isPending} placeholder="0,00" />
                    </Form.Group>
                  </Col>
                  <Col xs={6} md={4}>
                    <Form.Group>
                      <label className="nfe-dest-label">MVA %</label>
                      <Form.Control size="sm" type="number" step="0.01" {...register('mva', numField)} disabled={mutation.isPending} placeholder="0,00" />
                    </Form.Group>
                  </Col>
                  <Col xs={6} md={4}>
                    <Form.Group>
                      <label className="nfe-dest-label">Redução BC ST %</label>
                      <Form.Control size="sm" type="number" step="0.01" {...register('reducaoBCST', numField)} disabled={mutation.isPending} placeholder="0,00" />
                    </Form.Group>
                  </Col>
                  <Col xs={6} md={4}>
                    <Form.Group>
                      <label className="nfe-dest-label">Alíq. FCP ST %</label>
                      <Form.Control size="sm" type="number" step="0.01" {...register('aliquotaFCPST', numField)} disabled={mutation.isPending} placeholder="0,00" />
                    </Form.Group>
                  </Col>
                </>
              )}
            </Row>
          </div>

          {/* ── PIS / COFINS ─────────────────────────── */}
          <div className="nfe-dest-section">
            <div className="nfe-dest-section-header">
              <span className="nfe-dest-section-title">PIS / COFINS</span>
            </div>
            <Row className="g-3">
              <Col xs={12}>
                <span className="nfe-dest-section-title" style={{ fontSize: '0.62rem' }}>PIS</span>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <label className="nfe-dest-label">CST PIS</label>
                  <Controller
                    name="cstpis"
                    control={control}
                    render={({ field }) => (
                      <SelectCSTContribuicao size="sm" value={field.value ?? ''} onChange={field.onChange} disabled={mutation.isPending} />
                    )}
                  />
                </Form.Group>
              </Col>
              <Col xs={6} md={2}>
                <Form.Group>
                  <label className="nfe-dest-label">Base</label>
                  <Form.Control size="sm" type="number" step="0.01" {...register('baseCalculoPIS', numField)} disabled={mutation.isPending} placeholder="0,00" />
                </Form.Group>
              </Col>
              <Col xs={6} md={2}>
                <Form.Group>
                  <label className="nfe-dest-label">Alíquota %</label>
                  <Form.Control size="sm" type="number" step="0.01" {...register('aliquotaPIS', numField)} disabled={mutation.isPending} placeholder="0,00" />
                </Form.Group>
              </Col>
              <Col xs={6} md={2}>
                <Form.Group>
                  <label className="nfe-dest-label">Valor</label>
                  <Form.Control size="sm" type="number" step="0.01" {...register('valorPIS', numField)} disabled={mutation.isPending} placeholder="0,00" />
                </Form.Group>
              </Col>

              <Col xs={12}>
                <span className="nfe-dest-section-title" style={{ fontSize: '0.62rem' }}>COFINS</span>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <label className="nfe-dest-label">CST COFINS</label>
                  <Controller
                    name="cstcofins"
                    control={control}
                    render={({ field }) => (
                      <SelectCSTContribuicao size="sm" value={field.value ?? ''} onChange={field.onChange} disabled={mutation.isPending} />
                    )}
                  />
                </Form.Group>
              </Col>
              <Col xs={6} md={2}>
                <Form.Group>
                  <label className="nfe-dest-label">Base</label>
                  <Form.Control size="sm" type="number" step="0.01" {...register('baseCalculoCOFINS', numField)} disabled={mutation.isPending} placeholder="0,00" />
                </Form.Group>
              </Col>
              <Col xs={6} md={2}>
                <Form.Group>
                  <label className="nfe-dest-label">Alíquota %</label>
                  <Form.Control size="sm" type="number" step="0.01" {...register('aliquotaCOFINS', numField)} disabled={mutation.isPending} placeholder="0,00" />
                </Form.Group>
              </Col>
              <Col xs={6} md={2}>
                <Form.Group>
                  <label className="nfe-dest-label">Valor</label>
                  <Form.Control size="sm" type="number" step="0.01" {...register('valorCOFINS', numField)} disabled={mutation.isPending} placeholder="0,00" />
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* ── IPI ──────────────────────────────────── */}
          <div className="nfe-dest-section">
            <div className="nfe-dest-section-header">
              <span className="nfe-dest-section-title">IPI</span>
              <small className="text-body-secondary" style={{ fontSize: '0.72rem' }}>Opcional</small>
            </div>
            <Row className="g-3">
              <Col xs={12} md={6}>
                <Form.Group>
                  <label className="nfe-dest-label">CST IPI</label>
                  <Controller
                    name="cstipi"
                    control={control}
                    render={({ field }) => (
                      <SelectCSTIPI size="sm" value={field.value ?? ''} onChange={field.onChange} disabled={mutation.isPending} />
                    )}
                  />
                </Form.Group>
              </Col>
              <Col xs={6} md={2}>
                <Form.Group>
                  <label className="nfe-dest-label">Base</label>
                  <Form.Control size="sm" type="number" step="0.01" {...register('baseCalculoIPI', numField)} disabled={mutation.isPending} placeholder="0,00" />
                </Form.Group>
              </Col>
              <Col xs={6} md={2}>
                <Form.Group>
                  <label className="nfe-dest-label">Alíquota %</label>
                  <Form.Control size="sm" type="number" step="0.01" {...register('aliquotaIPI', numField)} disabled={mutation.isPending} placeholder="0,00" />
                </Form.Group>
              </Col>
              <Col xs={6} md={2}>
                <Form.Group>
                  <label className="nfe-dest-label">Valor</label>
                  <Form.Control size="sm" type="number" step="0.01" {...register('valorIPI', numField)} disabled={mutation.isPending} placeholder="0,00" />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-top border-opacity-25 py-2">
          <Button variant="phoenix-secondary" size="sm" onClick={() => !mutation.isPending && onHide()} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" size="sm" loading={mutation.isPending} loadingPosition="start">
            Salvar Tributação
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ModalTributacaoItemNFe;
